import toast from './toast'
import type {
  BridgeGetCurrentExpoPushTokenEvent,
  BridgeExternalLinkEvent,
  BridgePayload,
  BridgeRequestExpoPushTokenEvent,
  BridgeSignInEvent,
} from '../../../shared/bridgeActions'
import { loggedInRouter } from '@/router/loggedIn'
import { __DEV__ } from '@/constants'

declare global {
  interface Window {
    IS_MOBILE_APP: boolean
    DEVICE_NAME: string | null
    sentryTest: () => void
    handleBridgeResponse: (payload: BridgePayload) => void
    navigateToUrlFromMobileApp: (url: string) => void
    ReactNativeWebView: {
      postMessage: (message: string, targetOrigin: string) => void
    }
  }
}

const PromiseCache: Record<number, (data: any) => void> = {}

window.handleBridgeResponse = (payload: BridgePayload) => {
  const cachedPromise = PromiseCache[payload.promiseId]
  if (cachedPromise) {
    cachedPromise(payload.event.data)
    delete PromiseCache[payload.promiseId]
  }
}

window.navigateToUrlFromMobileApp = (url: string) => {
  try {
    if (!__DEV__ && !url.includes(window.location.host)) {
      // This URL is not from this ENV or DOMAIN, so we cannot navigate to it
      toast.error('Cannot navigate to external URLs within the app.')
      return
    }

    let finalUrl = url.split(window.location.host)[1]
    if (__DEV__ && finalUrl.includes('.com')) finalUrl = finalUrl.split('.com')[1] // workaround for dev env
    if (__DEV__ && finalUrl.includes(':5173')) finalUrl = finalUrl.split(':5173')[1] // workaround for dev env
    if (!finalUrl.startsWith('/')) finalUrl = `/${finalUrl}`
    if (finalUrl) loggedInRouter?.navigate(finalUrl)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to navigate to URL:', error)
    toast.error('Failed to open URL. Are you logged in?')
  }
}

const sendMessage = <T,>(event: BridgePayload['event']) => {
  const payload: BridgePayload = { promiseId: Date.now(), event }
  window.ReactNativeWebView.postMessage(JSON.stringify(payload), '*')
  return new Promise<T>(resolve => {
    PromiseCache[payload.promiseId] = resolve
  })
}

export const MobileAppBridge = {
  signIn: (provider: BridgeSignInEvent['request']['data']['provider']) => {
    return sendMessage<BridgeSignInEvent['response']['data']>({
      action: 'signIn', data: { provider }
    })
  },
  externalLink: async (url: string) => {
    const { success } = await sendMessage<BridgeExternalLinkEvent['response']['data']>({
      action: 'externalLink', data: { url }
    })
    if (!success) {
      toast.error('Failed to open link in external browser.')
    }
  },
  getCurrentExpoPushToken: async () => {
    const { token } = await sendMessage<BridgeGetCurrentExpoPushTokenEvent['response']['data']>({
      action: 'getCurrentExpoPushToken', data: {}
    })
    return token
  },
  requestExpoPushToken: async () => {
    const { token } = await sendMessage<BridgeRequestExpoPushTokenEvent['response']['data']>({
      action: 'requestExpoPushToken', data: {}
    })
    return token
  }
}
